import { For, Show, createMemo, createSignal } from "solid-js";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import {
  Actions,
  Button,
  CheckList,
  FieldGroup,
  Form,
  TextField,
  Toggle,
} from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { TreeChoose, type ChosenNode } from "../../components/TreeChoose.tsx";
import { DeleteButton } from "../../components/DeleteButton.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { useSession } from "../../session/provider.tsx";
import { CustomerSelect, OwnerTag } from "./CustomersPage.tsx";
import type { Customer, Persona, Product, View } from "../../api/domain.ts";
import styles from "./admin.module.scss";

const blankView = (): View => ({
  id: "",
  label: "",
  description: null,
  topics: [],
  capabilities: [],
  personas: [],
  products: [],
  questionnaires: [],
  customer: null,
});

/** The five lists a view narrows, each documented as "empty means all". */
const lists = ["topics", "capabilities", "personas", "products", "questionnaires"] as const;

type ListName = (typeof lists)[number];

/**
 * Which lists the user has switched off "all" for. This cannot be read back off
 * the view, because a narrowed list and an "all" list are both empty until
 * something is ticked, and the difference is what the user meant.
 */
type Narrowed = Record<ListName, boolean>;

const narrowedFrom = (view: View): Narrowed => ({
  topics: view.topics.length > 0,
  capabilities: view.capabilities.length > 0,
  personas: view.personas.length > 0,
  products: view.products.length > 0,
  questionnaires: view.questionnaires.length > 0,
});

/**
 * The personas a view of the given owner may name: an internal view any, a
 * customer's view only that customer's. The server refuses the rest, so the
 * form does not offer it.
 */
export const personasFor = (personas: readonly Persona[], owner: string | null): Persona[] =>
  owner === null ? [...personas] : personas.filter((persona) => persona.customer === owner);

/**
 * The products a view of the given owner may name: an internal view any active
 * one, a customer's view only those the customer lists. A product outside the
 * list would make the customer's report come out empty.
 */
export const productsFor = (
  products: readonly Product[],
  customers: readonly Customer[],
  owner: string | null,
): Product[] => {
  if (owner === null) return [...products];
  const listed = customers.find((customer) => customer.id === owner)?.products ?? [];
  return products.filter((product) => listed.includes(product.id));
};

/**
 * Views: the stored filters a report is built through, and until now the one
 * entity with no way to create it. A report needs a view, so the tool could not
 * be driven to its own output without reaching past the UI.
 */
export const ViewsPage = () => {
  const api = useApi();
  const i18n = useI18n();

  const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
  const tree = createLoader(() => api.tree({ lang: i18n.locale() }));
  const personas = createLoader(() => api.list("personas", { lang: i18n.locale() }));
  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  const questionnaires = createLoader(() => api.list("questionnaires"));
  const session = useSession();
  const customers = createLoader(() =>
    session.canWrite() ? api.list("customers") : Promise.resolve({ ok: true as const, value: [] }),
  );

  const [draft, setDraft] = createSignal<View | undefined>(undefined);
  const [narrowed, setNarrowed] = createSignal<Narrowed>(narrowedFrom(blankView()));
  const [problem, setProblem] = createSignal<string | undefined>(undefined);
  /** Set when an existing view is being edited, so saving patches instead of creating. */
  const [editing, setEditing] = createSignal(false);

  const begin = (view: View, existing: boolean) => {
    setDraft(view);
    setNarrowed(narrowedFrom(view));
    setEditing(existing);
    setProblem(undefined);
  };

  /** A list switched off "all" but left empty would silently mean "all" again. */
  const hollow = createMemo(() => {
    const view = draft();
    if (view === undefined) return [];
    return lists.filter((name) => narrowed()[name] && view[name].length === 0);
  });

  const nodes = (view: View): ChosenNode[] => [
    ...view.topics.map((id) => ({ kind: "topic" as const, id })),
    ...view.capabilities.map((id) => ({ kind: "capability" as const, id })),
  ];

  const setNodes = (view: View, chosen: ChosenNode[]) =>
    setDraft({
      ...view,
      topics: chosen.filter((node) => node.kind === "topic").map((node) => node.id),
      capabilities: chosen.filter((node) => node.kind === "capability").map((node) => node.id),
    });

  const setAll = (view: View, name: ListName, on: boolean) => {
    setNarrowed({ ...narrowed(), [name]: !on });
    // Switching "all" back on drops the picks, because that is what it means.
    if (on) setDraft({ ...view, [name]: [] });
  };

  const allowedPersonas = (view: View) =>
    personasFor(loadedValue(personas.value()) ?? [], view.customer);
  const allowedProducts = (view: View) =>
    productsFor(loadedValue(products.value()) ?? [], loadedValue(customers.value()) ?? [], view.customer);

  /** Changing the owner drops the picks the new owner may not name. */
  const setOwner = (view: View, customer: string | null) => {
    const next = { ...view, customer };
    const personaIds = allowedPersonas(next).map((entry) => entry.id);
    const productIds = allowedProducts(next).map((entry) => entry.id);
    setDraft({
      ...next,
      personas: next.personas.filter((id) => personaIds.includes(id)),
      products: next.products.filter((id) => productIds.includes(id)),
    });
  };

  const save = async (view: View) => {
    if (hollow().length > 0) return;
    if (view.label.trim() === "") return;
    const result = editing()
      ? await api.patch("views", view.id, view)
      : await api.create("views", view);
    if (!result.ok) {
      setProblem(i18n.t().error[result.error.kind === "server" ? "validation" : "network"]);
      return;
    }
    setDraft(undefined);
    views.reload();
  };

  const label = (name: ListName): string => i18n.t().views[name];

  /** The "all" switch and, when it is off, the picker it stands in for. */
  const Narrowable = (own: {
    view: View;
    name: ListName;
    children: (view: View) => ReturnType<typeof Form>;
  }) => (
    <FieldGroup label={label(own.name)} hint={i18n.t().views.allHint}>
      <Toggle
        label={`${i18n.t().common.all} ${label(own.name).toLowerCase()}`}
        checked={!narrowed()[own.name]}
        onToggle={(on) => setAll(own.view, own.name, on)}
      />
      <Show when={narrowed()[own.name]}>
        {own.children(own.view)}
        <Show when={hollow().includes(own.name)}>
          <p class={styles.problem}>{i18n.t().views.hollow}</p>
        </Show>
      </Show>
    </FieldGroup>
  );

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().views.title}</h1>
      </div>

      <Panel
        actions={
          <Button
            label={i18n.t().views.newView}
            onClick={() => begin(blankView(), false)}
          />
        }
      >
        <Show when={draft()}>
          {(view) => (
            <Form>
              <TextField
                label={i18n.t().common.label}
                value={view().label}
                onInput={(label) => setDraft({ ...view(), label })}
              />
              <TextField
                label={i18n.t().common.description}
                optional
                multiline
                value={view().description ?? ""}
                onInput={(description) =>
                  setDraft({ ...view(), description: description === "" ? null : description })
                }
              />
              <Show when={session.canWrite()}>
                <CustomerSelect
                  customers={loadedValue(customers.value())}
                  value={view().customer}
                  onPick={(customer) => setOwner(view(), customer)}
                />
              </Show>

              <Async value={tree.value()} onRetry={tree.reload}>
                {(topics) => (
                  <>
                    <Narrowable view={view()} name="topics">
                      {(current) => (
                        <TreeChoose
                          topics={topics}
                          kinds={["topic"]}
                          chosen={nodes(current).filter((node) => node.kind === "topic")}
                          onChange={(chosen) =>
                            setDraft({
                              ...current,
                              topics: chosen.map((node) => node.id),
                            })
                          }
                        />
                      )}
                    </Narrowable>
                    <Narrowable view={view()} name="capabilities">
                      {(current) => (
                        <TreeChoose
                          topics={topics}
                          kinds={["capability"]}
                          chosen={nodes(current).filter((node) => node.kind === "capability")}
                          onChange={(chosen) =>
                            setDraft({
                              ...current,
                              capabilities: chosen.map((node) => node.id),
                            })
                          }
                        />
                      )}
                    </Narrowable>
                  </>
                )}
              </Async>

              <Narrowable view={view()} name="personas">
                {(current) => (
                  <CheckList
                    label={label("personas")}
                    emptyLabel={i18n.t().common.empty}
                    choices={allowedPersonas(current).map((entry) => ({
                      id: entry.id,
                      label: entry.name,
                    }))}
                    picked={current.personas}
                    onChange={(personas) => setDraft({ ...current, personas })}
                  />
                )}
              </Narrowable>

              <Narrowable view={view()} name="products">
                {(current) => (
                  <CheckList
                    label={label("products")}
                    emptyLabel={i18n.t().common.empty}
                    choices={allowedProducts(current).map((entry) => ({
                      id: entry.id,
                      label: entry.name,
                    }))}
                    picked={current.products}
                    onChange={(products) => setDraft({ ...current, products })}
                  />
                )}
              </Narrowable>

              <Narrowable view={view()} name="questionnaires">
                {(current) => (
                  <CheckList
                    label={label("questionnaires")}
                    emptyLabel={i18n.t().common.empty}
                    choices={(loadedValue(questionnaires.value()) ?? []).map((entry) => ({
                      id: entry.id,
                      label: `${entry.author} (${entry.product ?? i18n.t().common.notAvailable})`,
                    }))}
                    picked={current.questionnaires}
                    onChange={(questionnaires) => setDraft({ ...current, questionnaires })}
                  />
                )}
              </Narrowable>

              <Show when={problem() !== undefined}>
                <p class={styles.problem}>{problem()}</p>
              </Show>

              <Actions>
                <Button
                  label={editing() ? i18n.t().common.save : i18n.t().common.create}
                  tone="primary"
                  disabled={hollow().length > 0 || view().label.trim() === ""}
                  onClick={() => void save(view())}
                />
                <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
              </Actions>
            </Form>
          )}
        </Show>

        <Async value={views.value()} onRetry={views.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(view) => view.id}
              emptyLabel={i18n.t().views.none}
              columns={[
                { key: "label", header: i18n.t().common.label, cell: (view) => view.label },
                ...(session.isCustomer()
                  ? []
                  : [
                      {
                        key: "customer",
                        header: i18n.t().common.customer,
                        cell: (view: View) => (
                          <OwnerTag customers={loadedValue(customers.value())} owner={view.customer} />
                        ),
                      },
                    ]),
                {
                  key: "narrows",
                  header: i18n.t().views.narrows,
                  cell: (view) => (
                    <span class={styles.rationale}>
                      {lists
                        .filter((name) => view[name].length > 0)
                        .map((name) => `${label(name)} (${view[name].length})`)
                        .join(", ") || i18n.t().views.everything}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  header: i18n.t().common.actions,
                  align: "end",
                  cell: (view) => (
                    <span class={styles.rowActions}>
                      <Button label={i18n.t().common.edit} onClick={() => begin(view, true)} />
                      <DeleteButton
                        collection="views"
                        id={view.id}
                        label={view.label}
                        onRemoved={views.reload}
                      />
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Async>
      </Panel>
    </>
  );
};
